import { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import { ICoach, IUpdateCoachInput } from "../../../types/api";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";
import { capitalizeWords } from "../../../shared/utility";
import { deleteCoach, updateCoach } from "../../../api/coaches/coaches";
import { toast } from "react-toastify";

interface IEditCoachPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
  coach?: ICoach;
}

function EditCoachPopup(props: IEditCoachPopupProps) {
  const { coach, showPopup } = props;

  const [coachData, setCoachData] = useState<ICoach>();
  const [initialCoachData, setInitialCoachData] = useState<ICoach>();
  const [showHandleDeleteCoachPopup, setShowHandleDeleteCoachPopup] =
    useState(false);
  const [showHandleUpdateCoachPopup, setShowHandleUpdateCoachPopup] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    props.onClose();
  };

  // Only send fields the admin actually changed.
  const getChangedFields = (): IUpdateCoachInput => {
    if (!coachData || !initialCoachData) return {};
    const changes: IUpdateCoachInput = {};
    if (coachData.first_name !== initialCoachData.first_name)
      changes.first_name = coachData.first_name;
    if (coachData.last_name !== initialCoachData.last_name)
      changes.last_name = coachData.last_name;
    if (coachData.coach_price !== initialCoachData.coach_price)
      changes.coach_price = coachData.coach_price;
    if (coachData.description !== initialCoachData.description)
      changes.description = coachData.description;
    if ((coachData.phone_number || "") !== (initialCoachData.phone_number || ""))
      changes.phone_number = coachData.phone_number || "";
    if (
      !!coachData.is_hitting_partner !== !!initialCoachData.is_hitting_partner
    )
      changes.is_hitting_partner = !!coachData.is_hitting_partner;
    return changes;
  };

  const hasChanges = Object.keys(getChangedFields()).length > 0;

  const handleEditCoach = async () => {
    if (!coachData) return;
    const changes = getChangedFields();
    if (Object.keys(changes).length === 0) return;

    setIsSaving(true);
    try {
      const response = await updateCoach(coachData.coach_id, changes);
      if (response) {
        toast.success("Tränare uppdaterad");
        props.onConfirm();
        handleClose();
      }
    } catch (error: any) {
      toast.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCoach = async () => {
    if (coach === undefined) return;

    try {
      const response = await deleteCoach(coach.coach_id);
      if (response && "message" in response === false) {
        props.onConfirm();
        handleClose();
      }
    } catch (error: any) {
      toast.error(error);
    }
  };

  useEffect(() => {
    if (coach && showPopup) {
      setInitialCoachData(coach);
      setCoachData({ ...coach });
    }
  }, [showPopup, coach]);

  if (!coach) return null;
  return (
    <Popup
      showPopup={props.showPopup}
      onClose={props.onClose}
      leftTopElement={
        <span className="text-m">
          Redigera {coach.is_hitting_partner ? "Hitting Partner" : "tränare"}{" "}
          {capitalizeWords(coach.first_name)}{" "}
          {capitalizeWords(coach.last_name)} {" (" + coach.coach_id + ")"}
        </span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
            setShowHandleUpdateCoachPopup(true);
          }}
        >
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              <label htmlFor="firstName" className="text-s">
                Förnamn
              </label>
              <input
                className="text-m"
                type="text"
                id="firstName"
                value={coachData?.first_name || ""}
                onChange={(e) =>
                  setCoachData({ ...coachData!, first_name: e.target.value })
                }
              />
            </div>
            <div className="general-popup-form-field">
              <label htmlFor="lastName" className="text-s">
                Efternamn
              </label>
              <input
                className="text-m"
                type="text"
                id="lastName"
                value={coachData?.last_name || ""}
                onChange={(e) =>
                  setCoachData({ ...coachData!, last_name: e.target.value })
                }
              />
            </div>
            <div className="general-popup-form-field">
              <label htmlFor="coachEmail" className="text-s">
                E-postadress (kan ej ändras)
              </label>
              <input
                className="text-m"
                type="email"
                id="coachEmail"
                value={coachData?.email || ""}
                disabled
              />
            </div>
            <div className="general-popup-form-field">
              <label htmlFor="coachPhone" className="text-s">
                Telefonnummer
              </label>
              <input
                className="text-m"
                type="tel"
                id="coachPhone"
                placeholder="Ange telefonnummer"
                value={coachData?.phone_number || ""}
                onChange={(e) =>
                  setCoachData({ ...coachData!, phone_number: e.target.value })
                }
              />
            </div>
            <div className="general-popup-form-field">
              <label htmlFor="coachPrice" className="text-s">
                Pris
              </label>
              <input
                className="text-m"
                type="number"
                id="coachPrice"
                min={0}
                value={coachData?.coach_price ?? ""}
                onChange={(e) =>
                  setCoachData({
                    ...coachData!,
                    coach_price: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="general-popup-form-field">
              <label htmlFor="coachDescription" className="text-s">
                Beskrivning
              </label>
              <input
                className="text-m"
                type="text"
                id="coachDescription"
                placeholder="Ange beskrivning"
                value={coachData?.description || ""}
                onChange={(e) =>
                  setCoachData({ ...coachData!, description: e.target.value })
                }
              />
            </div>
            <div className="general-popup-form-field">
              <label htmlFor="isHittingPartner" className="text-s">
                Hitting Partner
              </label>
              <div className="general-popup-flex-between">
                <span className="text-m secondary-text">
                  Är denna tränare en Hitting Partner?
                </span>
                <label className="toggle text-m">
                  <input
                    type="checkbox"
                    id="isHittingPartner"
                    checked={!!coachData?.is_hitting_partner}
                    onChange={(e) =>
                      setCoachData({
                        ...coachData!,
                        is_hitting_partner: e.target.checked,
                      })
                    }
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>
          <div className="general-popup-bottom-buttons-wrapper">
            <button
              className="deleteButton text-m"
              type="button"
              onClick={() => setShowHandleDeleteCoachPopup(true)}
            >
              Radera tränare permanent
            </button>
            <button className="text-m" type="submit" disabled={!hasChanges || isSaving}>
              {isSaving ? "Sparar…" : "Uppdatera"}
            </button>
          </div>
        </form>
      </div>

      <HandleConfirmationPopup
        showPopup={showHandleDeleteCoachPopup}
        onClose={() => setShowHandleDeleteCoachPopup(false)}
        onConfirm={() => {
          handleDeleteCoach();
          setShowHandleDeleteCoachPopup(false);
        }}
        questionText={`Är du säker på att du vill radera ${coach.first_name} (${coach.coach_id})?`}
        confirmText="Radera tränare permanent"
        denyText="Nej"
      />
      <HandleConfirmationPopup
        showPopup={showHandleUpdateCoachPopup}
        onClose={() => setShowHandleUpdateCoachPopup(false)}
        onConfirm={() => {
          handleEditCoach();
          setShowHandleUpdateCoachPopup(false);
        }}
        questionText={`Är du säker på att du vill uppdatera ${coach.first_name} (${coach.coach_id})?`}
        confirmText="Uppdatera tränare"
        denyText="Nej"
      />
    </Popup>
  );
}

export default EditCoachPopup;
